import { Dispatch } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Action } from 'redux';

import lang from '../../../lib/language';
import { SOROrderSide, SOROrderType } from '../../../lib/models/gateway/types';
import { RootState } from '../../../lib/store/index';
import { cancelOrder as serviceCancelOrder } from '../../../lib/store/trading/index';
import { uuidv4 } from '../../../lib/util/tools';

import { useTradingButtonsDisabled } from '../../../hooks';
import BaseModal from '../../../shared_components/BaseModal/BaseModal';
import Button from '../../../shared_components/Button/Button';
import { ButtonTypesEnum } from '../../../shared_components/Button/Button.types';
import useIsMobile from '../../../shared_components/hooks/useIsMobile';
import MobileDrawer from '../../../shared_components/MobileDrawer/MobileDrawer';

import styles from './CancelOrder.module.scss';

type Props = {
  orderId: string;
  isOpen: boolean;
  handleModalCancel: (isConfirmed: boolean) => void;
} & ReduxProps;

const CancelOrder = (props: Props) => {
  const { orderId, isOpen, handleModalCancel, pendingOrders, cancelOrder, accountStatusData } = props;
  const { isMobile } = useIsMobile();

  const pendingOrder = pendingOrders.find(el => el.parentOrderId === orderId);
  const isButtonsDisabled = useTradingButtonsDisabled({
    symbol: pendingOrder?.symbol ?? '',
    accountStatusData,
  });

  const onConfirm = () => {
    if (!pendingOrder) {
      return;
    }

    const { parentOrderId, side, symbol, type, totalQuantity } = pendingOrder;

    if (isMobile) {
      sessionStorage.setItem('showUseDetailsCancelModal', 'true');
    }
    cancelOrder({
      clientOrderId: uuidv4(),
      originalClientOrderId: parentOrderId,
      orderQty: totalQuantity,
      ordType: SOROrderType[type],
      side: SOROrderSide[side],
      symbol,
    });
    handleModalCancel(true);
  };

  const renderContent = () => (
    <>
      <div className={styles.text}>
        {lang.webOrderPopupAreYouSureToCancelLabel()}
      </div>
      <div className={styles.buttonsWrapper}>
        <Button
          fullWidth
          biggerFontSize
          disabled={isButtonsDisabled || !pendingOrder}
          onClick={onConfirm}
        >
          {lang.commonOrderSideButtonProceed2()}
        </Button>
        <Button
          variant={ButtonTypesEnum.Secondary}
          biggerFontSize
          fullWidth
          className={styles.btnCancel}
          onClick={() => handleModalCancel(false)}
        >
          {lang.commonOrderSideButtonKeepIt2()}
        </Button>
      </div>
    </>
  );

  if (isMobile) {
    return (
      <MobileDrawer
        isOpen={isOpen}
        title={lang.webOrderPopupCancelTitleLower()}
        onClose={() => handleModalCancel(false)}
        usePortal
      >
        {renderContent()}
      </MobileDrawer>
    );
  }

  return (
    <BaseModal isOpen={isOpen} toggle={() => handleModalCancel(false)}>
      <div className={styles.title}>
        {lang.webOrderPopupCancelTitleLower()}
      </div>
      {renderContent()}
    </BaseModal>
  );
};

const mapStateProps = (state: RootState) => ({
  pendingOrders: state.reporting.pendingOrders,
  requestStatus: state.trading.requestStatus,
  submittedOrder: state.trading.order,
  accountStatusData: state.closeout.accountStatusData,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  cancelOrder: (order) => dispatch(serviceCancelOrder(order)),
});

const connector = connect(mapStateProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(CancelOrder);
